import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Calendar,
  MapPin,
  ExternalLink,
  Facebook,
  ChevronDown,
  Filter,
  Bike,
  Footprints,
  Mountain,
  Clock,
  ZoomIn,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import HeroSection from "@/components/HeroSection";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const HERO_IMAGE = "/images/associazione-01-gruppo-montagna.jpg";

// ============================================
// TIPOLOGIE ATTIVITÀ
// ============================================

type ActivityType = "escursione" | "cicloturistica" | "visita" | "evento";

const activityTypes: Record<ActivityType, { label: string; icon: React.ElementType; color: string }> = {
  escursione: { label: "Escursione", icon: Footprints, color: "bg-green-500" },
  cicloturistica: { label: "Cicloturistica", icon: Bike, color: "bg-blue-500" },
  visita: { label: "Visita Culturale", icon: Mountain, color: "bg-amber-500" },
  evento: { label: "Evento", icon: Calendar, color: "bg-purple-500" },
};

// ============================================
// ATTIVITÀ 2025
// ============================================

interface Activity {
  image: string;
  title: string;
  date: string;
  dateSort: string;
  location: string;
  type: ActivityType;
  fbLink?: string;
}

const attivita2025: Activity[] = [
  {
    image: "/images/attivita-2025-12-07-natale-forcella.jpg",
    title: "Valle Interria e Complesso di Sant'Antonio",
    date: "7-8 Dicembre 2025",
    dateSort: "2025-12-07",
    location: "Valle Interria",
    type: "escursione",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid07uGsFjLbzLt29YJzwcuhYWahCn5zVbv3JJq3kEWG1261JxvDmd2Q8AhuchFzxZ1il",
  },
  {
    image: "/images/attivita-2025-11-02-collepietro.jpg",
    title: "Castello di Collepietro",
    date: "2 Novembre 2025",
    dateSort: "2025-11-02",
    location: "Collepietro (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0293gN5EVDCvfyvZhrdKN5VVKY4bu7LRaMctvucyNjhqNwTszGiPhJzad5WqivQHXfl",
  },
  {
    image: "/images/attivita-2025-10-12-piscignola-fondi.jpg",
    title: "Castello di Piscignola e Rocca di Fondi",
    date: "12 Ottobre 2025",
    dateSort: "2025-10-12",
    location: "Piscignola - Fondi (AQ)",
    type: "escursione",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid024x3a2gzBhXMWZVJ7NroBzXLPGDP8q2GCQzgGNAs5t86kZNVWSf2xnVzSNUAKc7Uzl",
  },
  {
    image: "/images/attivita-2025-07-05-territorio-bici-family.jpg",
    title: "Territorio in Bici - Family Experience",
    date: "5 Luglio 2025",
    dateSort: "2025-07-05",
    location: "L'Aquila e dintorni",
    type: "cicloturistica",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0NaRLZc2URgi4YqyBrJPwz1QBbWkCDDjbYQUthxxaffpxzT4NSEwJEMtqgcJqKxqRl",
  },
  {
    image: "/images/attivita-2025-05-25-cicloturistica-aquila-2ed.jpg",
    title: 'Cicloturistica "L\'Aquila Città Territorio"',
    date: "25 Maggio 2025",
    dateSort: "2025-05-25",
    location: "L'Aquila",
    type: "cicloturistica",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02Pt9PkpQPzZsYohzf9DG7VfTjaFSNpppF1oQ1Y2Fu8TaLMD4sUG7R2Xh3tsG1VkJHl",
  },
  {
    image: "/images/attivita-2025-05-18-castiglione-tornimparte.jpg",
    title: "Castello di Castiglione di Tornimparte",
    date: "18 Maggio 2025",
    dateSort: "2025-05-18",
    location: "Tornimparte (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0ZqyohKwaWH5S5GTq9obRmNABiSrHLMzxQ7Kgiq2iedCy2sxMbjSypwXGrxS6gDEgl",
  },
  {
    image: "/images/attivita-2025-04-23-rocca-san-marco.jpg",
    title: "San Marco di Preturo",
    date: "26 Aprile 2025",
    dateSort: "2025-04-26",
    location: "Preturo (AQ)",
    type: "escursione",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02Z9mu7y44rAevMpXnxR3rWeZ8sv2ujwJCdfpaFnTbf8VLP9uYjDSFKnX5grEDqgvDl",
  },
  {
    image: "/images/attivita-2025-04-06-cascina.jpg",
    title: "Castello di Cascina",
    date: "6 Aprile 2025",
    dateSort: "2025-04-06",
    location: "Cascina (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02GMcEtMGUSgPcpAY9AG6KCghMdjwm1EeE8SyJDpJXWPe8efy7yUrkZUyYaMvxj7wDl",
  },
];

// ============================================
// ATTIVITÀ 2024
// ============================================

const attivita2024: Activity[] = [
  {
    image: "/images/attivita-2025-12-07-natale-forcella.jpg",
    title: "Castello di Forcella",
    date: "22 Dicembre 2024",
    dateSort: "2024-12-22",
    location: "Forcella (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02koYMoe7f2FQHzusBTu5cYVD7GPAnU8JhCMU7gCBfTTxD97gDdDaGQnWxmGj6rU9tl",
  },
  {
    image: "/images/attivita-2024-04-27-san-marco-preturo.jpg",
    title: "Castello di San Benedetto in Perillis",
    date: "30 Novembre 2024",
    dateSort: "2024-11-30",
    location: "San Benedetto in Perillis (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02ptXX4CFVkD2vYLpimjLQyBMBeTohSMHwRwePU9YLnG4iY4nQTfYWsCxzwuYrgMgDl",
  },
  {
    image: "/images/attivita-2024-10-16-castiglione-rascino.jpg",
    title: "La Marrocchia, Non Solo Mais!",
    date: "2 e 9 Novembre 2024",
    dateSort: "2024-11-02",
    location: "L'Aquila",
    type: "evento",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0XbuZrBaZrn2azEyRJJPe6L6feFfBjNRNU7pfYy6FPNNfZLDt11qZbR6t5GqkETvel",
  },
  {
    image: "/images/attivita-2024-10-27-cicloturistica-castagna.png",
    title: "Cicloturistica della Castagna",
    date: "27 Ottobre 2024",
    dateSort: "2024-10-27",
    location: "Campotosto (AQ)",
    type: "cicloturistica",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0a11YDmuxMZDfy9SfMcfUsKdmjgKx1LYjeukgQqMhEvS4w1y1aFGwY4rS3Xv4TNjcl",
  },
  {
    image: "/images/attivita-2024-07-06-citta-acqua.jpg",
    title: 'Ciclo Escursione "La Città e l\'Acqua"',
    date: "6 Luglio 2024",
    dateSort: "2024-07-06",
    location: "L'Aquila",
    type: "cicloturistica",
    fbLink:
      "https://www.facebook.com/alessandro.serreli.75/posts/pfbid02kbTPjNMLB7LuWCSagBGB2jRMUaB7ST5MSAAzQDvMrwuRMocTtEd4ZDSNmc8h9FyVl",
  },
  {
    image: "/images/attivita-2024-06-29-barisciano.jpg",
    title: "Castello di Barisciano",
    date: "29 Giugno 2024",
    dateSort: "2024-06-29",
    location: "Barisciano (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0M83VA8izZ5HRHoMbDCaTdhky1AMWUGY9fkm6B1Zy3AzrovuRVWa1icK4D45yEGb7l",
  },
  {
    image: "/images/attivita-2024-06-29-gran-sasso.jpg",
    title: "Castelli del Gran Sasso",
    date: "29 Giugno 2024",
    dateSort: "2024-06-29",
    location: "Gran Sasso (AQ)",
    type: "escursione",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid013wuj8ugHNNya6V43wEaPLQr6wKGgpu6d6Vxx4PJSvAmuRpxirsW6NRZcVM1XBwTl",
  },
  {
    image: "/images/attivita-2024-05-25-fontecchio.jpg",
    title: "Castello di Fontecchio",
    date: "25 Maggio 2024",
    dateSort: "2024-05-25",
    location: "Fontecchio (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0XNUTmFkHfdrJFCmY7Y4RFYzdedWyuYWAWLb4unUKHprcBeqzhQ7rFhRvxnJivX3cl",
  },
  {
    image: "/images/attivita-2024-05-19-cicloturistica-aquila.jpg",
    title: "L'Aquila in Bici e Cicloturistica del Territorio",
    date: "19 Maggio 2024",
    dateSort: "2024-05-19",
    location: "L'Aquila",
    type: "cicloturistica",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0PhoBwbYxnp3kmihPUfDfcQpXB64QrucWHMy9fnAVfC8eWtdATw2QYJtCyXDMnZMRl",
  },
  {
    image: "/images/attivita-2024-04-27-san-marco-preturo.jpg",
    title: "San Marco di Preturo: Origini, Personaggi e Tradizioni",
    date: "27 Aprile 2024",
    dateSort: "2024-04-27",
    location: "Preturo (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02qXb1YWBHyucYvKthu2mNTdVqK8V9hNn7ocFCbmrsb4KyHaMx5H36mpMEQsukzAcol",
  },
  {
    image: "/images/attivita-2025-04-06-cascina.jpg",
    title: "Castello di Picenze",
    date: "7 Aprile 2024",
    dateSort: "2024-04-07",
    location: "Picenze (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02tWuN48wdJRw6RjDaAbiYKdmP74cWfRhq7xbaHRk3xmhyss61LgES7MFVepLSZJBKl",
  },
];

// ============================================
// ATTIVITÀ 2023
// ============================================

const attivita2023: Activity[] = [
  {
    image: "/images/attivita-2023-10-01-valle-vasto.jpg",
    title: "Castelli Piana di Navelli",
    date: "28 Ottobre 2023",
    dateSort: "2023-10-28",
    location: "Piana di Navelli (AQ)",
    type: "escursione",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0WDeAQzKeTxMZRyjuWtif4ThKoTFcRkdieE9aNy7LhJEbC3tFtWYvspPkpKHQ7pThl",
  },
  {
    image: "/images/attivita-2023-10-01-valle-vasto.jpg",
    title: "Castelli della Valle del Vasto",
    date: "1 Ottobre 2023",
    dateSort: "2023-10-01",
    location: "Valle del Vasto (AQ)",
    type: "escursione",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid07uGsFjLbzLt29YJzwcuhYWahCn5zVbv3JJq3kEWG1261JxvDmd2Q8AhuchFzxZ1il",
  },
  {
    image: "/images/associazione-01-gruppo-montagna.jpg",
    title: "Conosciamo Amiternum",
    date: "3 Settembre 2023",
    dateSort: "2023-09-03",
    location: "Amiternum (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02hxDPdZKNedf73rNkXDNQmDeZSRm7ZdytesPctjgfgJjb2WcmWs5FpYcmuAYYgQHNl",
  },
  {
    image: "/images/home-slider-04-castello-torre.jpg",
    title: "Castello di San Vittorino",
    date: "18 Agosto 2023",
    dateSort: "2023-08-18",
    location: "San Vittorino (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0JQRCeByZdCvdugYxtuQPeyL4K6JEhVxbcvX1vu6YkjWfbvzacyDjwNR3mKXKiCD6l",
  },
  {
    image: "/images/home-slider-03-rovine-albero.jpg",
    title: "Castello di Preturo",
    date: "6 Agosto 2023",
    dateSort: "2023-08-06",
    location: "Preturo (AQ)",
    type: "visita",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0TCsBsTcApmaGajUAtSHvQUD2VbStBEVk6xZjKZRBZrWa89i8F47B1rLbz29UxCQgl",
  },
  {
    image: "/images/attivita-2023-06-17-aquila-in-bici.jpg",
    title: "L'Aquila in Bici",
    date: "17 Giugno 2023",
    dateSort: "2023-06-17",
    location: "L'Aquila",
    type: "cicloturistica",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0DKfD1xJk7bgdxFL3xAhY7Z13J6QhDkck49mZ4VuhiUAe2SAmsXLeAgRuirvjbFVrl",
  },
  {
    image: "/images/attivita-2023-05-21-media-valle-aterno.jpg",
    title: "Castelli Media Valle dell'Aterno",
    date: "21 Maggio 2023",
    dateSort: "2023-05-21",
    location: "Valle dell'Aterno (AQ)",
    type: "escursione",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid0PhoBwbYxnp3kmihPUfDfcQpXB64QrucWHMy9fnAVfC8eWtdATw2QYJtCyXDMnZMRl",
  },
  {
    image: "/images/attivita-2023-04-16-cascina-cesura.jpg",
    title: "Castelli di Cascina e Cesura",
    date: "7 Maggio 2023",
    dateSort: "2023-05-07",
    location: "Cascina - Cesura (AQ)",
    type: "cicloturistica",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid02mPffjphSLHDoL2k7m4yxUHSyeEPrWPjpQaahugecsAUXcJ4dQd8o7v1v4gU8w4ytl",
  },
  {
    image: "/images/attivita-2025-04-23-rocca-san-marco.jpg",
    title: "Rocca di San Marco",
    date: "23 Aprile 2023",
    dateSort: "2023-04-23",
    location: "San Marco (AQ)",
    type: "escursione",
    fbLink:
      "https://www.facebook.com/99castelli/posts/pfbid024x3a2gzBhXMWZVJ7NroBzXLPGDP8q2GCQzgGNAs5t86kZNVWSf2xnVzSNUAKc7Uzl",
  },
];

// ============================================
// COMPONENTE LIGHTBOX
// ============================================

interface LightboxProps {
  image: string;
  title: string;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ image, title, onClose }) => {
  // Chiudi con ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      {/* Pulsante chiudi */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Chiudi"
      >
        <X size={32} className="text-white" />
      </button>

      {/* Titolo */}
      <div className="absolute top-4 left-4 z-10 max-w-[70%]">
        <h3 className="text-white text-xl font-heading font-bold drop-shadow-lg">{title}</h3>
      </div>

      {/* Immagine */}
      <motion.img
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        src={image}
        alt={title}
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Istruzioni */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        Premi ESC o clicca fuori per chiudere
      </div>
    </motion.div>
  );
};

// ============================================
// COMPONENTE CARD ATTIVITÀ
// ============================================

interface ActivityCardProps {
  activity: Activity;
  index: number;
  onImageClick: (activity: Activity) => void;
}

const ActivityCard: React.FC<ActivityCardProps> = ({ activity, index, onImageClick }) => {
  const typeInfo = activityTypes[activity.type];
  const TypeIcon = typeInfo.icon;

  // Genera un ID univoco per l'attività basato sul titolo
  const activityId = `activity-${activity.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")}`;

  return (
    <motion.div
      id={activityId}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group bg-white rounded-xl overflow-hidden shadow-elegant hover:shadow-elegant-lg transition-all duration-300 border border-gray-100 flex flex-col"
    >
      {/* Immagine - Cliccabile per aprire lightbox */}
      <div className="relative overflow-hidden cursor-pointer" onClick={() => onImageClick(activity)}>
        <img
          src={activity.image}
          alt={activity.title}
          className="w-full h-auto object-contain bg-gray-50 transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {/* Icona zoom in alto a destra su hover */}
        <div className="absolute top-3 right-3 p-2 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <ZoomIn size={20} className="text-white" />
        </div>
      </div>

      {/* Contenuto */}
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="text-lg font-heading font-bold text-primary mb-3 line-clamp-2 group-hover:text-primary-light transition-colors">
          {activity.title}
        </h3>

        <div className="space-y-2 mb-4 flex-1">
          <div className="flex items-center gap-2 text-sm text-text-light">
            <Calendar size={16} className="text-primary flex-shrink-0" />
            <span>{activity.date}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-text-light">
            <MapPin size={16} className="text-primary flex-shrink-0" />
            <span className="line-clamp-1">{activity.location}</span>
          </div>
        </div>

        {activity.fbLink && (
          <a
            href={activity.fbLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-lg font-medium transition-colors text-sm"
          >
            <Facebook size={18} />
            <span>Vedi su Facebook</span>
          </a>
        )}
      </div>
    </motion.div>
  );
};

// ============================================
// COMPONENTE SEZIONE ANNO
// ============================================

interface YearSectionProps {
  year: string;
  activities: Activity[];
  defaultExpanded?: boolean;
  forceExpanded?: boolean;
  onImageClick: (activity: Activity) => void;
}

const YearSection: React.FC<YearSectionProps> = ({
  year,
  activities,
  defaultExpanded = false,
  forceExpanded = false,
  onImageClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || forceExpanded);
  const [hasAnimated, setHasAnimated] = useState(defaultExpanded); // Skip initial animation if defaultExpanded

  // Se forceExpanded cambia, aggiorna lo stato
  useEffect(() => {
    if (forceExpanded) {
      setIsExpanded(true);
    }
  }, [forceExpanded]);

  // Dopo il primo toggle, abilita le animazioni
  const handleToggle = () => {
    setHasAnimated(true);
    setIsExpanded(!isExpanded);
  };

  // Statistiche
  const stats = {
    total: activities.length,
    escursioni: activities.filter((a) => a.type === "escursione").length,
    cicloturistiche: activities.filter((a) => a.type === "cicloturistica").length,
    visite: activities.filter((a) => a.type === "visita").length,
  };

  return (
    <div id={`year-${year}`} className="mb-8">
      {/* Header Anno */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center justify-between bg-white rounded-xl p-5 shadow-elegant hover:shadow-elegant-lg transition-all border border-gray-100 group"
      >
        <div className="flex items-center gap-4">
          <div className="bg-primary text-white text-2xl font-heading font-bold px-4 py-2 rounded-lg">{year}</div>
          <div className="text-left">
            <h3 className="text-xl font-heading font-bold text-foreground">{stats.total} Attività</h3>
            <p className="text-sm text-text-light">
              {stats.escursioni > 0 && `${stats.escursioni} escursion${stats.escursioni > 1 ? "i" : "e"}`}
              {stats.escursioni > 0 && stats.cicloturistiche > 0 && " • "}
              {stats.cicloturistiche > 0 &&
                `${stats.cicloturistiche} cicloturistic${stats.cicloturistiche > 1 ? "he" : "a"}`}
              {(stats.escursioni > 0 || stats.cicloturistiche > 0) && stats.visite > 0 && " • "}
              {stats.visite > 0 && `${stats.visite} visit${stats.visite > 1 ? "e" : "a"}`}
            </p>
          </div>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="bg-primary/10 p-2 rounded-full group-hover:bg-primary/20 transition-colors"
        >
          <ChevronDown size={24} className="text-primary" />
        </motion.div>
      </button>

      {/* Griglia Attività - 3 col desktop, 2 tablet, 1 mobile */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={hasAnimated ? { opacity: 0, height: 0 } : false}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activities.map((activity, index) => (
                <ActivityCard
                  key={activity.dateSort + activity.title}
                  activity={activity}
                  index={index}
                  onImageClick={onImageClick}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================
// COMPONENTE STATISTICHE
// ============================================

const StatsBar: React.FC = () => {
  const totalAttivita = attivita2025.length + attivita2024.length + attivita2023.length;
  const totalCicloturistiche = [...attivita2025, ...attivita2024, ...attivita2023].filter(
    (a) => a.type === "cicloturistica",
  ).length;
  const totalEscursioni = [...attivita2025, ...attivita2024, ...attivita2023].filter(
    (a) => a.type === "escursione",
  ).length;
  const totalVisite = [...attivita2025, ...attivita2024, ...attivita2023].filter((a) => a.type === "visita").length;

  const stats = [
    { label: "Attività totali", value: totalAttivita, icon: Calendar },
    { label: "Escursioni", value: totalEscursioni, icon: Footprints },
    { label: "Cicloturistiche", value: totalCicloturistiche, icon: Bike },
    { label: "Visite culturali", value: totalVisite, icon: Mountain },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl p-4 shadow-elegant text-center border border-gray-100"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-3">
              <Icon size={24} className="text-primary" />
            </div>
            <div className="text-3xl font-heading font-bold text-primary mb-1">{stat.value}</div>
            <div className="text-sm text-text-light">{stat.label}</div>
          </motion.div>
        );
      })}
    </div>
  );
};

// ============================================
// COMPONENTE PRINCIPALE
// ============================================

// Funzione helper per generare ID attività
const generateActivityId = (title: string) =>
  `activity-${title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")}`;

// Funzione per trovare l'anno di un'attività dato il titolo
const findActivityYear = (title: string): string | null => {
  const allActivities = [
    { year: "2025", activities: attivita2025 },
    { year: "2024", activities: attivita2024 },
    { year: "2023", activities: attivita2023 },
  ];

  for (const { year, activities } of allActivities) {
    if (activities.some((a) => a.title === title)) {
      return year;
    }
  }
  return null;
};

const Attivita: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lightboxActivity, setLightboxActivity] = useState<Activity | null>(null);
  const [highlightYear, setHighlightYear] = useState<string | null>(null);

  // Leggi il parametro activity o year dall'URL
  useEffect(() => {
    const activityParam = searchParams.get("activity");
    const yearParam = searchParams.get("year");

    if (activityParam) {
      // Trova l'anno dell'attività
      const year = findActivityYear(activityParam);
      if (year) {
        setHighlightYear(year);
        // Rimuovi il parametro dall'URL
        setSearchParams({});

        // Genera l'ID dell'elemento e scrolla dopo che la sezione si è espansa
        const activityId = generateActivityId(activityParam);
        setTimeout(() => {
          const element = document.getElementById(activityId);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            // Evidenzia brevemente l'elemento
            element.classList.add("ring-4", "ring-primary", "ring-opacity-50");
            setTimeout(() => {
              element.classList.remove("ring-4", "ring-primary", "ring-opacity-50");
            }, 2000);
          }
        }, 500);
      }
    } else if (yearParam && ["2023", "2024", "2025"].includes(yearParam)) {
      setHighlightYear(yearParam);
      setSearchParams({});
      setTimeout(() => {
        const yearElement = document.getElementById(`year-${yearParam}`);
        if (yearElement) {
          yearElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
    }
  }, [searchParams, setSearchParams]);

  return (
    <PageLayout>
      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxActivity && (
          <Lightbox
            image={lightboxActivity.image}
            title={lightboxActivity.title}
            onClose={() => setLightboxActivity(null)}
          />
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <HeroSection
        backgroundImage={HERO_IMAGE}
        title="Le Nostre Attività"
        subtitle="Escursioni, cicloturistiche e visite culturali alla scoperta dei castelli d'Abruzzo"
        height="medium"
        overlay
        centered
      />

      {/* Intro */}
      <Section background="cream">
        <div className="max-w-4xl mx-auto text-center">
          <SectionHeader title="Calendario Attività" subtitle="Tutti gli eventi organizzati da ASD 99 Castelli" />
          <p className="text-text-light font-body leading-relaxed mb-8 text-lg">
            Dal <strong className="text-primary">2023</strong> organizziamo escursioni a piedi, cicloturistiche e visite
            culturali per esplorare i <strong className="text-primary">99 castelli</strong> dell'aquilano e i borghi
            medievali dell'Abruzzo. Tutte le attività sono guidate da accompagnatori esperti e coperte da
            <strong className="text-primary"> assicurazione CSI</strong>.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button href="https://www.facebook.com/99Castelli" variant="primary" icon={<Facebook size={18} />}>
              Seguici su Facebook
            </Button>
            <Button href="/tesseramento" variant="outline">
              Diventa Socio
            </Button>
          </div>
        </div>
      </Section>

      {/* Statistiche */}
      <Section background="white">
        <div className="max-w-6xl mx-auto">
          <StatsBar />
        </div>
      </Section>

      {/* Timeline Attività per Anno */}
      <Section background="gray">
        <div className="max-w-6xl mx-auto">
          <SectionHeader title="Archivio Attività" subtitle="Esplora tutte le attività organizzate anno per anno" />

          {/* 2025 */}
          <YearSection
            year="2025"
            activities={attivita2025}
            defaultExpanded={true}
            forceExpanded={highlightYear === "2025"}
            onImageClick={setLightboxActivity}
          />

          {/* 2024 */}
          <YearSection
            year="2024"
            activities={attivita2024}
            forceExpanded={highlightYear === "2024"}
            onImageClick={setLightboxActivity}
          />

          {/* 2023 */}
          <YearSection
            year="2023"
            activities={attivita2023}
            forceExpanded={highlightYear === "2023"}
            onImageClick={setLightboxActivity}
          />
        </div>
      </Section>

      {/* Prossimi Eventi */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-8 md:p-12 text-center text-white relative overflow-hidden">
            {/* Pattern decorativo */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
            </div>

            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
                <Clock size={32} className="text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Prossimi Eventi</h2>
              <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
                Per conoscere le prossime attività in programma, segui la nostra pagina Facebook dove pubblichiamo tutti
                gli eventi e le locandine.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  href="https://www.facebook.com/99Castelli"
                  variant="secondary"
                  size="lg"
                  icon={<Facebook size={20} />}
                >
                  Segui su Facebook
                </Button>
                <Button href="https://wa.me/393402894567" variant="outlineLight" size="lg">
                  Contattaci su WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA Tesseramento */}
      <Section background="primary">
        <div className="text-center max-w-3xl mx-auto">
          <SectionHeader
            title="Vuoi Partecipare?"
            subtitle="Per partecipare alle nostre attività è necessario essere tesserati"
            light
          />
          <p className="text-white/80 mb-8 text-lg">
            Il tesseramento include la copertura assicurativa CSI e ti permette di partecipare a tutte le escursioni e
            cicloturistiche organizzate dall'associazione.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button href="/tesseramento" variant="secondary" size="lg">
              Scopri il Tesseramento
            </Button>
            <Button href="/associazione" variant="outlineLight" size="lg">
              Chi Siamo
            </Button>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

export default Attivita;
